// Copyright 2018 Sorama B.V.

import { bindable, bindingMode, computedFrom } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { Unit, UnitValue } from "units/Unit";

/**
 * Part that visualizes an indicator
 */
export class IndicatorPart extends PlotPartBase {
    //#region Bindables
    @bindable public mode = "x";
    @bindable public stroke = "carrot";
    @bindable public strokeopacity = 1.0;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public value: UnitValue;
    //#endregion

    static instanceCounter = 0;
    private instanceNumber: number;

    private svgElement: SVGElement;
    private mouseSelection = false;

    private textCenter: number;
    private x0: number;
    private x1: number;
    private y0: number;
    private y1: number;
    private sliderValue: number;

    constructor() {
        super(0);
        this.instanceNumber = IndicatorPart.instanceCounter++;
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
        this.valueChanged();
    }

    public attached(): void {
        super.attached();

        this.plotParent.addWindowInputEventListener("mouseup", this.onMouseUp);
        this.plotParent.addWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.addWindowInputEventListener("touchmove", this.touchMove);
        this.plotParent.addWindowInputEventListener("touchUp", this.onMouseUp);
    }

    public detached(): void {
        this.plotParent.addWindowInputEventListener("touchmove", this.touchMove);
        this.plotParent.addWindowInputEventListener("touchUp", this.onMouseUp);
        this.plotParent.addWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.addWindowInputEventListener("mouseup", this.onMouseUp);

        super.detached();
    }

    public onSize(size: Size, margin: Margin): void {
        super.onSize(size, margin);
        this.valueChanged();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        super.onViewPortComputed(viewPort, viewPortBounds, units);
        this.valueChanged();
    }

    //#region Changed Handler
    private modeChanged(): void {
        this.valueChanged();
    }

    private valueChanged(): void {
        if (this.value == null || this.value === undefined || !this.sourceUnits || !this.actualSize || !this.viewPort)
            return;

        if (this.mode === "x") {
            if (!this.sourceUnits.x) return;

            this.x0 = this.dataToSvgX(this.value.toUnit(this.sourceUnits.x));
            this.x1 = this.x0;
            this.y0 = 0;
            this.y1 = this.actualSize.height;
        } else {
            // Y
            if (!this.sourceUnits.y) return;

            this.x0 = 0;
            this.x1 = this.actualSize.width;
            this.y0 = this.dataToSvgY(this.value.toUnit(this.sourceUnits.y));
            this.y1 = this.y0;
        }
    }
    //#endregion

    //#region mouse/touch handlers

    private mouseDown = (event: MouseEvent): void => {
        this.mouseSelection = true;
    };

    private onMouseUp = (event: MouseEvent): void => {
        this.mouseSelection = false;
    };

    private onMouseMove = (event: MouseEvent): void => {
        if (!this.svgElement) return;

        if (this.mouseSelection && event.buttons % 2 === 1) {
            const x = event.clientX - this.svgElement.getBoundingClientRect().left;

            this.value = new UnitValue(this.viewToDataX(x), this.value.unit);
        } else {
            this.mouseSelection = false;
        }
    };

    private touchMove = (event: TouchEvent): void => {
        const element = <HTMLElement>event.touches[0].target;
        this.value = new UnitValue(
            this.viewToDataX(event.touches[0].pageX - element.getBoundingClientRect().left),
            this.value.unit
        );
    };

    //#endregion

    //#region UI Helpers
    @computedFrom("value", "viewPort.x", "viewPort.width")
    private get isIndicatorVisible(): boolean {
        if (!this.sourceUnits || !this.viewPort || !this.sourceUnits || !this.sourceUnits.x) return false;

        return (
            !!this.value &&
            this.value.toUnit(this.sourceUnits.x) >= this.viewPort.x &&
            this.value.toUnit(this.sourceUnits.x) <= this.viewPort.x + this.viewPort.width
        );
    }

    @computedFrom("value", "viewPort.x", "viewPort.width", "viewPort.height", "actualSize.width", "actualSize.height")
    public get computeSelectedValueBackgroundPath(): string {
        if (
            !this.actualSize ||
            !this.viewPort ||
            !this.isIndicatorVisible ||
            !this.value ||
            !this.sourceUnits ||
            !this.sourceUnits.x
        )
            return "";

        const value = this.dataToSvgX(this.value.toUnit(this.sourceUnits.x));

        const backgroundWidth = 40;

        let start = value - backgroundWidth / 2;
        let end = value + backgroundWidth / 2;

        const arrowSize = 10;
        let arrowStart = value - arrowSize / 2;
        let arrowEnd = value + arrowSize / 2;

        // Prevent fliepeltje from going over the left
        if (start < -1) {
            start = -1;
            end = start + backgroundWidth;
        }

        // Prevent fliepeltje from going over the right
        if (end > this.actualSize.width + 2) {
            end = this.actualSize.width + 2;
            start = end - backgroundWidth;
        }

        // Prevent arrow from going over the left
        if (arrowStart < -1) {
            arrowStart = -1;
        }

        // Prevent arrow from going over the right
        if (arrowEnd > this.actualSize.width + 2) {
            arrowEnd = this.actualSize.width + 2;
        }

        this.textCenter = (end + start) / 2;

        return (
            `${start},${this.actualSize.height + 15} ` +
            `${start},${this.actualSize.height + 1} ` +
            `${arrowStart},${this.actualSize.height + 1} ` +
            `${value},${this.actualSize.height - 5} ` +
            `${arrowEnd},${this.actualSize.height + 1} ` +
            `${end},${this.actualSize.height + 1} ` +
            `${end},${this.actualSize.height + 15}`
        );
    }

    //#endregion

    //#region helpers
    private viewToDataX(x: number): number {
        if (x < 0) x = 0;
        if (x > this.actualSize.width) x = this.actualSize.width;

        return (x / this.actualSize.width) * this.viewPort.width + this.viewPort.x;
    }

    private dataToSvgX(x: number): number {
        return ((x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width;
    }

    private dataToSvgY(y: number): number {
        return (1 - (y - this.viewPort.y) / this.viewPort.height) * this.actualSize.height;
    }
    //#endregion

    private updateCurrentTime(): void {
        this.value = new UnitValue(this.sliderValue, this.value.unit);
    }
}
